import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Account, AccountsState } from '../types';
import { OnboardingWelcomeStep } from './onboarding/OnboardingWelcomeStep';
import { OnboardingFollowUpStep } from './onboarding/OnboardingFollowUpStep';
import { JoinClassroomPrompt } from './JoinClassroomPrompt';
import { LinkChildPrompt } from './LinkChildPrompt';
import { CreateFirstClassroom } from './CreateFirstClassroom';
import { DjayAvatar, DjayBubble } from '../../mascot';
import type { OnboardingStepState } from '../types';
import { OnboardingProgress } from './onboarding/OnboardingProgress';
interface OnboardingWizardProps {
  account: Account;
  accounts: AccountsState;
  onJoinClassroom: (joinCode: string) => {
    success: boolean;
    error?: string;
  };
  onLinkChild: (linkCode: string) => {
    success: boolean;
    error?: string;
  };
  onCreateClassroom: (name: string, grade: number) => void;
  onJoinExistingClassroom?: (classroomId: string, mode: 'transfer' | 'coteach') => void;
  onCompleteOnboarding: (stepState?: OnboardingStepState) => void;
  onNavigateToModule?: (moduleId: string) => void;
  onNavigateToMyClass?: () => void;
}
export function OnboardingWizard({
  account,
  accounts,
  onJoinClassroom,
  onLinkChild,
  onCreateClassroom,
  onJoinExistingClassroom,
  onCompleteOnboarding,
  onNavigateToModule,
  onNavigateToMyClass
}: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [groupLevel, setGroupLevel] = useState(account.role === 'learner' && account.grade ? account.grade : 6);
  const [stepState, setStepState] = useState<OnboardingStepState>({
    welcome: null,
    action: null,
    followUp: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const handleSkipAction = () => {
    const newState = {
      ...stepState,
      action: 'skipped' as const,
      followUp: 'skipped' as const
    };
    setStepState(newState);
    onCompleteOnboarding(newState); // Skip step 3 if step 2 is skipped
  };
  const handleCompleteFollowUp = () => {
    const newState = {
      ...stepState,
      followUp: 'completed' as const
    };
    setStepState(newState);
    onCompleteOnboarding(newState);
  };
  const getBackgroundClass = () => {
    switch (account.role) {
      case 'learner':
        return 'bg-gradient-to-b from-orange-50 via-amber-50/30 to-white';
      case 'parent':
        return 'bg-gradient-to-b from-emerald-50 via-teal-50/30 to-white';
      case 'teacher':
        return 'bg-gradient-to-b from-indigo-50 via-blue-50/30 to-white';
      default:
        return 'bg-slate-50';
    }
  };
  const getGradientClass = () => {
    switch (account.role) {
      case 'learner':
        return 'from-orange-400 to-amber-400';
      case 'parent':
        return 'from-emerald-400 to-teal-400';
      case 'teacher':
        return 'from-indigo-500 to-blue-400';
      default:
        return 'from-slate-400 to-slate-500';
    }
  };
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <OnboardingWelcomeStep key="step0" role={account.role} name={account.displayName} groupLevel={groupLevel} onNext={() => {
          setStepState({
            ...stepState,
            welcome: 'completed'
          });
          if (account.role === 'learner') {
            setCurrentStep(2);
          } else {
            setCurrentStep(1);
          }
        }} onGroupChange={setGroupLevel} data-id="element-360" />;
      case 1:
        return <motion.div key="step1" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="w-full max-w-md mx-auto" data-id="element-361">
            {/* Mascot section for step 1 */}
            <div className="flex flex-col items-center mb-6 relative" data-id="element-362">
              <motion.div animate={{
              y: [0, -8, 0]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-363">
                <DjayAvatar emotion={account.role === 'learner' ? 'thinking' : account.role === 'parent' ? 'thinking' : 'celebrating'} size="lg" data-id="element-364" />
              </motion.div>
              <div className="mt-3 w-full max-w-[260px]" data-id="element-365">
                <DjayBubble message={account.role === 'learner' ? 'Heb je een koppelcode van je meester of juf gekregen? Vul hem hier in om in je klas te komen!' : account.role === 'parent' ? 'Vul de koppelcode van je kind in om hun voortgang te kunnen volgen.' : 'Laten we je eerste klas aanmaken.'} position="top" colorScheme={account.role === 'learner' ? 'amber' : account.role === 'parent' ? 'emerald' : 'indigo'} autoDismissMs={0} data-id="element-366" />
              </div>
            </div>

            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-lg border border-slate-200 relative overflow-hidden" data-id="element-367">
              <div className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${getGradientClass()}`} data-id="element-368" />
              <OnboardingProgress currentStep={1} role={account.role} data-id="element-369" />

              {account.role === 'learner' && <div className="space-y-4" data-id="element-370">
                  <JoinClassroomPrompt embedded onJoin={code => {
                setIsLoading(true);
                setError(undefined);
                const result = onJoinClassroom(code);
                setIsLoading(false);
                if (result.success) {
                  setStepState({
                    ...stepState,
                    action: 'completed'
                  });
                  setCurrentStep(2);
                } else {
                  setError(result.error || 'Code niet gevonden. Probeer opnieuw.');
                }
              }} onSkip={handleSkipAction} isLoading={isLoading} error={error} data-id="element-371" />
                  <div className="text-center" data-id="element-372">
                    <button onClick={handleSkipAction} disabled={isLoading} className="w-full py-2 text-sm font-medium text-slate-500 hover:text-orange-600 transition-colors" data-id="element-373">
                      Sla over voor nu
                    </button>
                    <p className="text-xs text-slate-400 mt-1" data-id="element-374">
                      Je kunt later altijd een klas joinen via Instellingen.
                    </p>
                  </div>
                </div>}

              {account.role === 'parent' && <div className="space-y-4" data-id="element-375">
                  <LinkChildPrompt embedded onLink={code => {
                setIsLoading(true);
                setError(undefined);
                const result = onLinkChild(code);
                setIsLoading(false);
                if (result.success) {
                  setStepState({
                    ...stepState,
                    action: 'completed'
                  });
                  setCurrentStep(2);
                } else {
                  setError(result.error || 'Code niet gevonden. Probeer opnieuw.');
                }
              }} onSkip={handleSkipAction} isLoading={isLoading} error={error} data-id="element-376" />
                  <div className="text-center" data-id="element-377">
                    <button onClick={handleSkipAction} disabled={isLoading} className="w-full py-2 text-sm font-medium text-slate-500 hover:text-emerald-600 transition-colors" data-id="element-378">
                      Sla over voor nu
                    </button>
                    <p className="text-xs text-slate-400 mt-1" data-id="element-379">
                      U kunt later koppelen via Instellingen.
                    </p>
                  </div>
                </div>}

              {account.role === 'teacher' && <div className="space-y-4" data-id="element-380">
                  <CreateFirstClassroom embedded accounts={accounts} activeTeacherId={account.id} onCreate={(name, grade) => {
                setIsLoading(true);
                setError(undefined);
                onCreateClassroom(name, grade);
                setIsLoading(false);
                setStepState({
                  ...stepState,
                  action: 'completed'
                });
                setCurrentStep(2);
              }} onJoinExisting={onJoinExistingClassroom ? (classroomId, mode) => {
                setIsLoading(true);
                setError(undefined);
                onJoinExistingClassroom(classroomId, mode);
                setIsLoading(false);
                setStepState({
                  ...stepState,
                  action: 'completed'
                });
                setCurrentStep(2);
              } : undefined} onSkip={handleSkipAction} isLoading={isLoading} error={error} data-id="element-381" />
                  <div className="text-center" data-id="element-382">
                    <button onClick={handleSkipAction} disabled={isLoading} className="w-full py-2 text-sm font-medium text-slate-500 hover:text-indigo-600 transition-colors" data-id="element-383">
                      Sla over voor nu
                    </button>
                    <p className="text-xs text-slate-400 mt-1" data-id="element-384">
                      Zonder klas kunt u geen leerlingen beheren. U kunt later
                      een klas aanmaken via Instellingen.
                    </p>
                  </div>
                </div>}
            </div>
          </motion.div>;
      case 2:
        return <OnboardingFollowUpStep key="step2" role={account.role} groupLevel={groupLevel} onComplete={handleCompleteFollowUp} onNavigateToModule={onNavigateToModule} onNavigateToMyClass={onNavigateToMyClass} data-id="element-385" />;
      default:
        return null;
    }
  };
  return <div className={`min-h-screen ${getBackgroundClass()} flex flex-col items-center justify-center p-4 sm:p-8 overflow-hidden`} data-id="element-386">
      <AnimatePresence mode="wait" data-id="element-387">{renderStep()}</AnimatePresence>
    </div>;
}